import { createFetcher } from '@/services/createFetcher';
import type {
  ContentPerformanceFetcherParams,
  EpisodeLaunchComparisonFetcherParams,
} from '@/services/query';
import type {
  ContentOverviewResponse,
  EpisodeLaunchComparisonResponse,
  EpisodePublishDatesResponse,
  HourlyPlaysHour,
  ItemPlatformsResponse,
} from '@/types/content';

/**
 * Resolves the `networkIds` query value for the multi-network `/v2/content/*` endpoints.
 * Prefers the explicit `networkIds` list (query-mode content performance); falls back to
 * `[networkId]` so single-network callers keep working unchanged. Returns a comma-separated
 * string (e.g. `a,b,c`) or `undefined` when no network is available.
 */
const toNetworkIdsParam = (
  networkIds: string[] | undefined,
  networkId: string | undefined
): string | undefined => {
  const rawIds = networkIds && networkIds.length > 0 ? networkIds : networkId ? [networkId] : [];
  // Trim, drop blanks and dedupe before sending to the API so callers can't produce malformed
  // values like `?networkIds=,net-1` or duplicate ids.
  const ids = [...new Set(rawIds.map((id) => id.trim()).filter(Boolean))];
  return ids.length > 0 ? ids.join(',') : undefined;
};

const createContentFetcher = <T>(
  endpoint: string | ((params: ContentPerformanceFetcherParams) => string),
  pathParamKeys: (keyof ContentPerformanceFetcherParams)[] = []
) => {
  return ({ networkId, networkIds, from, to, ...rest }: ContentPerformanceFetcherParams) => {
    const allParams = { networkId, networkIds, from, to, ...rest };
    const resolvedEndpoint = typeof endpoint === 'function' ? endpoint(allParams) : endpoint;
    const path = `/v2/content/${resolvedEndpoint}`;

    const queryRest = Object.fromEntries(
      Object.entries(rest).filter(
        ([key]) => !pathParamKeys.includes(key as keyof ContentPerformanceFetcherParams)
      )
    );
    const query = { networkIds: toNetworkIdsParam(networkIds, networkId), from, to, ...queryRest };
    return createFetcher<T>(path, query);
  };
};

export const contentOverviewFetcher = createContentFetcher<ContentOverviewResponse>('overview');

/**
 * The last 24 UTC hours of listens and views.
 * The browser converts `hour_utc` to local time. This does not send the page date range.
 */
export const hourlyPlaysFetcher = ({
  networkId,
  networkIds,
  showIds,
}: {
  networkId?: string;
  networkIds?: string[];
  showIds?: string[];
}) => {
  return createFetcher<HourlyPlaysHour[]>('/v2/content/hourly-plays', {
    networkIds: toNetworkIdsParam(networkIds, networkId),
    ...(showIds && showIds.length > 0 ? { showIds: showIds.join(',') } : {}),
  });
};

export const episodePublishDatesFetcher =
  createContentFetcher<EpisodePublishDatesResponse>('episodes/publish-dates');

export const showPlatformsFetcher = createContentFetcher<ItemPlatformsResponse>('shows/platforms');

/** Fetches post-launch comparison data for episodes across one or more filtered shows. */
export const episodeLaunchComparisonFetcher = ({
  networkIds,
  showIds,
  episodeIds,
  windowDays,
  dataSource,
}: EpisodeLaunchComparisonFetcherParams) => {
  const path = `/v2/content/episode-launch-comparison`;
  return createFetcher<EpisodeLaunchComparisonResponse>(path, {
    networkIds: networkIds.join(','),
    episodeIds: episodeIds.join(','),
    ...(showIds && showIds.length > 0 && { showIds: showIds.join(',') }),
    windowDays,
    dataSource,
  });
};

